import {
  ApolloClient,
  ApolloLink,
  type DefaultOptions,
  HttpLink,
  InMemoryCache,
  Observable,
} from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { isAbortError } from '../Utils/isAbortError';
import {
  formatFriendlyNetworkErrorMessage,
  isHtmlPayload,
  isJsonParseError,
} from '../Utils/isJsonParseError';
import defaults from '../apollo/Defaults';
import { REFRESH_TOKEN } from '../graphql/Authentication/mutation';

import {
  categoryVar,
  configurationGuideVar,
  dialogManyOrgVar,
  selectedOrganizationVar,
} from '../graphql/local/Category/vars';
import { GET_ERROR_INFO } from '../graphql/local/ErrorInfo/query';
import {
  GET_HOST_FIELSD_BY_LR_ID,
  GET_ISINFORMHOSTFIELDS,
} from '../graphql/local/HostFields/query';
import { GET_SNACKBAR_STATE } from '../graphql/local/snackBar/query';
import {
  GET_LEGAL_INFORMATION,
  GET_MY_IDENTITY,
  GET_MY_ORGANIZATION,
  GET_TUNNEL_DATA,
} from '../graphql/local/Tunnel/query';
import { type UserInterface } from '../Interface/UserInterface';
import AuthService from '../Services/AuthService';
import { RefreshToken } from '../Services/RefreshToken';
import { createLoginUrlWithCallback } from '../Utils/createLoginUrlWithCallback';
import config from './config';

const redirectToLogin = () => {
  if (!window.location.pathname.includes('logout')) {
    window.location.href = `${window.location.origin}/auth/logout?${createLoginUrlWithCallback()}`;
  }
};

const logoutLink: ApolloLink = onError(({ error }) => {
  if (CombinedGraphQLErrors.is(error) && error.errors.length > 0) {
    const graphqlError = error.errors[0];

    if (
      (graphqlError.extensions && graphqlError.extensions.code === 'UNAUTHENTICATED') ||
      (graphqlError.message && /AuthenticationError/gi.test(graphqlError.message.toString()))
    ) {
      const retryCount = parseInt(sessionStorage.getItem('auth_reload_retry') || '0', 10);
      if (retryCount < 2) {
        sessionStorage.setItem('auth_reload_retry', String(retryCount + 1));
        const { refreshToken } = RefreshToken.getTRefreshToken();
        if (refreshToken) {
          getRefreshedAccessTokenPromise()
            .then(() => {
              window.location.reload();
            })
            .catch(() => {
              sessionStorage.removeItem('auth_reload_retry');
              redirectToLogin();
            });
        } else {
          sessionStorage.removeItem('auth_reload_retry');
          redirectToLogin();
        }
      } else {
        sessionStorage.removeItem('auth_reload_retry');
        redirectToLogin();
      }
    }
  } else {
    // Gestion des erreurs réseau (ex: ServerParseError / réponse HTML reçue au lieu de JSON)
    const networkError = (error as any)?.networkError || error;
    if (isJsonParseError(networkError)) {
      const statusCode =
        (networkError as any)?.statusCode || (networkError as any)?.response?.status;
      if (statusCode === 401 || statusCode === 403) {
        redirectToLogin();
      }
    }
  }
}) as ApolloLink;

const authLink = setContext((_: any, prevContext: any) => {
  const { headers } = prevContext;
  const token = AuthService.getAccessToken();
  const switchUser = AuthService.getSwitchUser();
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      switchuser: switchUser ? switchUser : '',
    },
  };
});

/**
 * Fetch personnalisé qui intercepte les réponses HTML (ex: 502 Bad Gateway Nginx, WAF, 401)
 * et les transforme en réponse JSON GraphQL valide avec message compréhensible
 * avant qu'Apollo ne tente de parser le body et ne lève une JSON Parse error: Unrecognized token '<'.
 */
export const customFetch: typeof fetch = async (uri, options) => {
  const response = await fetch(uri, options);
  const contentType = response.headers.get('content-type') || '';

  const mightBeHtml =
    contentType.includes('text/html') ||
    contentType.includes('text/plain') ||
    response.status >= 400 ||
    !contentType.includes('json');

  if (mightBeHtml) {
    try {
      const clone = response.clone();
      const text = await clone.text();

      if (isHtmlPayload(text)) {
        const errorMessage = formatFriendlyNetworkErrorMessage(response.status);
        const code = response.status === 401 ? 'UNAUTHENTICATED' : 'SERVER_ERROR';

        const safeGraphqlResponse = JSON.stringify({
          errors: [
            {
              message: errorMessage,
              extensions: {
                code,
                statusCode: response.status,
              },
            },
          ],
        });

        if (typeof Response !== 'undefined') {
          return new Response(safeGraphqlResponse, {
            status: 200,
            statusText: 'OK',
            headers: {
              'Content-Type': 'application/json',
            },
          });
        }
      }
    } catch {
      // Laisser passer la réponse originale si la lecture échoue
    }
  }

  return response;
};

const httpLink = new HttpLink({
  uri: config.serverUri,
  fetch: customFetch,
});

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        category: {
          read() {
            return categoryVar();
          },
        },
        selectedOrganization: {
          read() {
            return selectedOrganizationVar();
          },
        },
        configurationGuide: {
          read() {
            return configurationGuideVar();
          },
        },
        dialogManyOrg: {
          read() {
            return dialogManyOrgVar();
          },
        },
      },
    },
    // Normalisation des entités par leur identifiant unique
    Organization: { keyFields: ['id'] },
    OrganizationLite: { keyFields: ['id'] },
    OrganizationLiteV2: { keyFields: ['id'] },
    Letter: { keyFields: ['id'] },
    AllLetter: { keyFields: ['id'] },
    Person: { keyFields: ['id'] },
    Document: { keyFields: ['id'] },
    Store: { keyFields: ['id'] },
    Product: { keyFields: ['id'] },
    SubscriptionType: { keyFields: ['id'] },
    LegalRepresentative: { keyFields: ['id'] },
    UserInterface: { keyFields: ['id'] },
  },
});

// Link pour intercepter les requêtes annulées (navigation, unmount) et éviter les erreurs réseau non gérées
const abortFilterLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      next: (result) => observer.next(result),
      error: (networkError) => {
        if (isAbortError(networkError)) {
          observer.complete();
          return;
        }
        observer.error(networkError);
      },
      complete: () => observer.complete(),
    });

    return () => {
      subscription.unsubscribe();
    };
  });
});

const link = ApolloLink.from([abortFilterLink, logoutLink, authLink.concat(httpLink)]);

const defaultOptions: DefaultOptions = {
  mutate: {
    errorPolicy: 'all',
  },
} as unknown as DefaultOptions;

const apolloClient = new ApolloClient({
  defaultOptions,
  link,
  cache,
  dataMasking: false,
});

// Write default local state to cache (replaces apollo-link-state defaults)
function writeDefaults() {
  cache.writeQuery({ query: GET_SNACKBAR_STATE, data: { snackBar: defaults.snackBar } });
  // Category, selectedOrganization, configurationGuide, and dialogManyOrg
  // are now managed by reactive variables (see vars.ts). No cache writes needed.
  categoryVar(defaults.category);
  const currentOrg = selectedOrganizationVar();
  if (!currentOrg?.id) {
    selectedOrganizationVar(defaults.selectedOrganization);
  }
  configurationGuideVar(defaults.configurationGuide);
  dialogManyOrgVar(defaults.dialogManyOrg);
  cache.writeQuery({
    query: GET_HOST_FIELSD_BY_LR_ID,
    variables: { lrid: 0 },
    data: { hostFields: defaults.hostFields },
  });
  cache.writeQuery({
    query: GET_ISINFORMHOSTFIELDS,
    data: { informHostFields: defaults.informHostFields },
  });
  cache.writeQuery({ query: GET_ERROR_INFO, data: { errorInfo: defaults.errorInfo } });
  cache.writeQuery({
    query: GET_LEGAL_INFORMATION,
    data: { legalInformation: defaults.legalInformation },
  });
  cache.writeQuery({ query: GET_MY_IDENTITY, data: { myIdentity: defaults.myIdentity } });
  cache.writeQuery({
    query: GET_MY_ORGANIZATION,
    data: { myOrganization: defaults.myOrganization },
  });
  cache.writeQuery({ query: GET_TUNNEL_DATA, data: { tunnelData: defaults.tunnelData } });
}
writeDefaults();
apolloClient.onResetStore(async () => writeDefaults());

const getRefreshedAccessTokenPromise = async () => {
  try {
    const actualToken = AuthService.getAccessToken();
    const { refreshToken, username } = RefreshToken.getTRefreshToken();
    if (refreshToken && username) {
      const { data, error: mutateError } = await apolloClient.mutate<
        {
          refreshToken: {
            refreshToken: string;
            token: string;
            tokenDuration: number;
            tokenExpirationDate: Date;
            user: UserInterface;
          };
        },
        { refreshToken: string; username: string }
      >({
        mutation: REFRESH_TOKEN,
        variables: {
          refreshToken,
          username,
        },
      });

      if (data && data.refreshToken) {
        const {
          token,
          refreshToken: newRefreshToken,
          tokenDuration,
          tokenExpirationDate,
          user,
        } = data.refreshToken;
        if (token) {
          AuthService.setAccessToken(token);
          AuthService.setUserId(user.id);
        }

        RefreshToken.setRefreshToken({
          refreshToken: newRefreshToken,
          token,
          tokenDuration,
          tokenExpirationDate,
          username: (user && user.email) || '',
        });
        return data.refreshToken.token;
      }

      if (mutateError) {
        throw mutateError;
      }

      return actualToken;
    }

    throw new Error('No refresh token');
  } catch (error) {
    throw error instanceof Error ? error : new Error(String(error));
  }
};

export default apolloClient;
